import React from 'react';
import { Box } from '@mui/material';

// ============================================================================
//  StatusPill — single source of truth for status rendering across the app.
//
//  Inventory status enum values (from DB):
//    PRODUCED, REPACK-IN, REPACK-OUT, REPROCESS-IN, QC-SAMPLE-IN,
//    SHIPMENT-PENDING, SHIPPED, EXCESS-RETURNED, RE-PRINTING, RE-PROCESS,
//    QC SAMPLE, ONLINE MIX, FCL EXCESS
//
//  Document status values:
//    DRAFT, PENDING_APPROVAL, APPROVED, EXECUTED, RETURNED, CANCELLED
//
//  Usage:
//    <StatusPill status="PRODUCED" />
//    <StatusPill status="REPACK-OUT" size="small" />
// ============================================================================

const STATUS_STYLES = {
  // Inventory in-states
  'PRODUCED':         { bg: '#dcfce7', fg: '#15803d' },
  'REPACK-IN':        { bg: '#dbeafe', fg: '#1d4ed8' },
  'REPROCESS-IN':     { bg: '#dbeafe', fg: '#1d4ed8' },
  'QC-SAMPLE-IN':     { bg: '#dbeafe', fg: '#1d4ed8' },
  // Inventory out-states (in process)
  'REPACK-OUT':       { bg: '#ffedd5', fg: '#9a3412' },
  'RE-PRINTING':      { bg: '#fce7f3', fg: '#9d174d' },
  'RE-PROCESS':       { bg: '#f3e8ff', fg: '#6b21a8' },
  'QC SAMPLE':        { bg: '#cffafe', fg: '#155e75' },
  // Inventory final states
  'SHIPMENT-PENDING': { bg: '#fef3c7', fg: '#92400e' },
  'SHIPPED':          { bg: '#ede9fe', fg: '#5b21b6' },
  'EXCESS-RETURNED':  { bg: '#fee2e2', fg: '#991b1b' },
  'ONLINE MIX':       { bg: '#e0e7ff', fg: '#3730a3' },
  'FCL EXCESS':       { bg: '#fee2e2', fg: '#991b1b' },
  // Document statuses
  'DRAFT':            { bg: '#f1f5f9', fg: '#334155' },
  'PENDING_APPROVAL': { bg: '#fef3c7', fg: '#92400e' },
  'APPROVED':         { bg: '#dcfce7', fg: '#15803d' },
  'EXECUTED':         { bg: '#dbeafe', fg: '#1d4ed8' },
  'RETURNED':         { bg: '#dbeafe', fg: '#1d4ed8' },
  'CANCELLED':        { bg: '#fee2e2', fg: '#991b1b' },
};

const SIZE_STYLES = {
  small:  { fontSize: 10,    py: 0.25, px: 1,    dotSize: 5 },
  medium: { fontSize: 11,    py: 0.5,  px: 1.25, dotSize: 6 },
  large:  { fontSize: 12.5,  py: 0.75, px: 1.75, dotSize: 7 },
};

export default function StatusPill({ status, size = 'medium', sx: extra = {} }) {
  const style = STATUS_STYLES[status] || { bg: '#f1f5f9', fg: '#475569' };
  const sz = SIZE_STYLES[size] || SIZE_STYLES.medium;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        bgcolor: style.bg, color: style.fg,
        borderRadius: 12,
        fontSize: sz.fontSize, fontWeight: 700, letterSpacing: 0.2,
        py: sz.py, px: sz.px,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        ...extra,
      }}
    >
      <Box
        component="span"
        sx={{
          width: sz.dotSize, height: sz.dotSize,
          borderRadius: '50%',
          bgcolor: 'currentColor', opacity: 0.7,
        }}
      />
      {status || '—'}
    </Box>
  );
}
